import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, ArrowLeft, Trophy, BarChart3, Info } from "lucide-react";
import { TEAMS } from "../lib/site";
import { matchesApi, stableSig } from "../lib/api";
import TeamBadge from "../components/TeamBadge";
import { scoreStr } from "../lib/format";
import BetSlip from "../components/BetSlip";
import SessionMarket from "../components/SessionMarket";
import TossMarket from "../components/TossMarket";
import Odds from "../components/Odds";
import { useApp } from "../context/AppContext";
import { inr } from "../lib/format";
import { netPnlByOutcome } from "../lib/exposure";

export default function MatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { bets } = useApp();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState(null); // { market, option }
  const [tab, setTab] = useState("session");

  useEffect(() => {
    setLoading(true);
    let active = true;
    let lastSig = "";
    let poll = null;
    const apply = (m) => {
      if (!active) return;
      const sig = stableSig(m);
      if (sig !== lastSig) { lastSig = sig; setMatch(m); }
      setLoading(false);
    };
    const load = () => matchesApi.get(id).then(apply)
      .catch(() => { if (active) { setMatch(null); setLoading(false); } });
    const startPoll = () => { if (!poll && active) poll = setInterval(load, 1000); };
    const stopPoll = () => { if (poll) { clearInterval(poll); poll = null; } };
    load();
    // Live push: the server streams the match the instant the Jitex sync writes it.
    // Any stream hiccup falls back to the 1s poll until the stream delivers again.
    let es = null;
    if (typeof EventSource !== "undefined") {
      es = new EventSource(`${process.env.REACT_APP_BACKEND_URL}/api/matches/${id}/stream`);
      es.onmessage = (e) => { try { stopPoll(); apply(JSON.parse(e.data)); } catch { /* ignore bad frame */ } };
      es.onerror = () => startPoll();
    } else {
      startPoll();
    }
    return () => { active = false; stopPoll(); if (es) es.close(); };
  }, [id]);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading match…</div>;
  }

  if (!match) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="text-muted-foreground">Match not found.</p>
        <Link to="/sports" className="text-primary font-semibold">Back to sports</Link>
      </div>
    );
  }

  const home = match.homeTeam || TEAMS[match.home] || { code: match.home, name: match.home };
  const away = match.awayTeam || TEAMS[match.away] || { code: match.away, name: match.away };
  const isCompleted = match.status === "completed";
  const bettingDisabled = isCompleted || !!match.bettingSuspended;

  const pick = (market, option, side = "back") => {
    if (bettingDisabled) return;
    const rate = side === "lay" ? option.lay : (option.back ?? option.odds);
    if (!rate) return;
    setSelection({
      matchId: match.id,
      matchLabel: `${home.code || match.home} vs ${away.code || match.away}`,
      marketKey: market.key,
      marketName: market.name,
      selectionId: option.id,
      selectionLabel: option.label,
      odds: rate,
      side,
    });
  };


  const winner = (match.markets || []).find((mk) => mk.key === "winner");
  const others = (match.markets || []).filter((mk) => mk.key !== "winner");
  const tabs = [
    { key: "session", label: "Session", show: !!match.sessionMarket?.over },
    { key: "lambi", label: "Lambi", show: !!match.lambiMarket?.over },
    { key: "toss", label: "Toss", show: !!(match.tossMarket?.open && match.tossMarket?.closeAt) },
  ].filter((t) => t.show);
  const activeTab = tabs.some((t) => t.key === tab) ? tab : (tabs[0]?.key || null);

  const renderWinner = (market) => {
    const outcomes = market.options.map((o) => o.label);
    const openWinner = (bets || []).filter((b) => b.matchId === match.id && b.marketName === market.name && b.status === "matched");
    const pnl = openWinner.length ? netPnlByOutcome(openWinner, outcomes) : {};
    // Dead-market guard (mirrors backend): one-sided ~1.01 runner, crossed quote, or absurd spread ⇒ disable the WHOLE market.
    const marketDead = market.suspended || market.options.some((o) => {
      const b = o.back, l = o.lay;
      if (!b && l && l <= 1.10) return true;
      if (!l && b && b <= 1.10 && "lay" in o) return true;
      if (b && l && (l < b - 0.001 || l > b * 1.25)) return true;
      return false;
    });
    const held = !!market.held;
    return (
      <div data-testid="market-winner" className="bg-white rounded-[3px] overflow-hidden shadow-sm">
        <div className="grid grid-cols-[1fr_72px_72px] items-center border-b border-slate-200 text-slate-700">
          <div className="flex items-center gap-2 px-3 py-2 min-w-0">
            <span className="grid place-items-center h-6 w-6 rounded-full bg-slate-200 text-slate-600"><BarChart3 className="h-3.5 w-3.5" /></span>
            <div className="min-w-0 leading-tight">
              <div className="text-[11px] text-slate-500">{match.status === "live" ? "In-Play" : "Pre-match"}</div>
              <div className="text-[12px] font-semibold truncate">{market.name}</div>
            </div>
            {openWinner.length > 0 && (
              <Link to="/my-bets" data-testid="live-position-pill" className="ml-1 inline-flex items-center gap-1 rounded-full border border-emerald-500/50 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 whitespace-nowrap">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 live-dot" /> Live position
              </Link>
            )}
          </div>
          <div className="text-center text-[13px] font-semibold py-2">Lagai</div>
          <div className="text-center text-[13px] font-semibold py-2">Khai</div>
        </div>
        {market.options.map((o) => {
          const backActive = selection && selection.marketKey === market.key && selection.selectionId === o.id && selection.side === "back";
          const layActive = selection && selection.marketKey === market.key && selection.selectionId === o.id && selection.side === "lay";
          const isWinner = isCompleted && o.won;
          const v = openWinner.length ? Math.round(pnl[o.label] || 0) : null;
          // When Khai (lay) is 0.01/0.02/0.03 the selection is effectively decided — lock Lagai (back).
          const backLocked = o.lay != null && o.lay > 0 && o.lay < 0.035;
          const susp = (o.suspended || backLocked || marketDead) && !held;
          return (
            <div key={o.id} className="grid grid-cols-[1fr_72px_72px] border-b border-slate-200 last:border-b-0">
              <div className="flex flex-col justify-center px-3 py-1.5 min-w-0 text-slate-900">
                <span className="text-[14px] font-semibold truncate">{o.label}{isWinner && <span className="ml-2 text-xs text-emerald-600">✓ Won</span>}</span>
                {v !== null && (
                  <span data-testid={`pnl-${o.id}`} className={`text-[11px] font-semibold ${v > 0 ? "text-emerald-600" : v < 0 ? "text-rose-600" : "text-slate-500"}`}>{v > 0 ? "+" : ""}{inr(v)}</span>
                )}
                {susp && <span data-testid={`suspended-${o.id}`} className="text-[10px] font-semibold uppercase tracking-wide text-amber-600">Suspended</span>}
              </div>
              <button data-testid={`back-${o.id}`} onClick={() => pick(market, o, "back")} disabled={bettingDisabled || !o.back || backLocked || marketDead}
                className={`h-[46px] flex flex-col items-center justify-center leading-none border-l border-white transition-colors disabled:cursor-not-allowed ${
                  held ? "bg-zinc-300" : backActive ? "bg-[#5AAEEA] ring-2 ring-inset ring-sky-800" : "bg-[#72BBEF] hover:bg-[#5AAEEA] disabled:bg-slate-200"}`}>
                <span className={`font-display font-extrabold text-[16px] ${held ? "text-zinc-500" : "text-slate-900"}`}>{o.back && (!marketDead || held) ? <Odds value={o.back} showArrow /> : "—"}</span>
                <span className="text-[9px] text-slate-800/80 mt-0.5">Lagai</span>
              </button>
              {o.lay && (!marketDead || held) ? (
                <button data-testid={`lay-${o.id}`} onClick={() => pick(market, o, "lay")} disabled={bettingDisabled || marketDead}
                  className={`h-[46px] flex flex-col items-center justify-center leading-none border-l border-white transition-colors disabled:cursor-not-allowed ${
                    held ? "bg-zinc-300" : layActive ? "bg-[#F77FAE] ring-2 ring-inset ring-rose-700" : "bg-[#F994BA] hover:bg-[#F77FAE] disabled:bg-slate-200"}`}>
                  <span className={`font-display font-extrabold text-[16px] ${held ? "text-zinc-500" : "text-slate-900"}`}><Odds value={o.lay} showArrow /></span>
                  <span className="text-[9px] text-slate-800/80 mt-0.5">Khai</span>
                </button>
              ) : <div className="h-[46px] grid place-items-center bg-slate-200 text-slate-400 text-xs border-l border-white">—</div>}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto bg-[#eef1f4] min-h-[70vh] sm:my-5 sm:rounded-lg sm:overflow-hidden lg:pb-28" data-testid="match-detail-page">
      {/* Breadcrumb bar */}
      <div className="flex items-center gap-2 bg-[#2b2f36] text-slate-300 px-3 py-2 text-[13px]">
        <button onClick={() => navigate(-1)} aria-label="Back" data-testid="match-back-btn" className="grid place-items-center h-6 w-6 rounded-full bg-white/10 text-white"><ArrowLeft className="h-4 w-4" /></button>
        <span>Cricket</span><span className="text-slate-500">/</span>
        <span className="text-white font-semibold truncate">{home.name} v {away.name}</span>
      </div>

      {/* Match strip: series, status, scores */}
      <div className="bg-white border-b border-slate-200 px-3 py-2" data-testid="match-header">
        <div className="flex items-center justify-between text-[11px] text-slate-500">
          <span className="truncate pr-2">{match.series} · {match.format}</span>
          {match.status === "live"
            ? <span className="inline-flex items-center gap-1 text-[#1e5bd6] font-bold shrink-0"><span className="h-1.5 w-1.5 rounded-full bg-red-500 live-dot" /> In-Play</span>
            : <span className="shrink-0">{match.matchDateStr} {match.matchTimeStr}</span>}
        </div>
        <div className="mt-1.5 grid grid-cols-2 gap-2 text-slate-900">
          {[[home, match.score?.home, match.home, match.homeTeam], [away, match.score?.away, match.away, match.awayTeam]].map(([t, sc, code, team]) => (
            <div key={code} className="flex items-center gap-2 min-w-0">
              <TeamBadge code={code} team={team} size={26} />
              <div className="min-w-0 leading-tight">
                <div className="text-[13px] font-semibold truncate">{t.name}</div>
                {sc && <div className="text-[12px] font-display font-bold text-slate-700">{scoreStr(sc)}</div>}
              </div>
            </div>
          ))}
        </div>
        {(match.result || match.venue) && (
          <div className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-500 truncate">
            {isCompleted ? <><Trophy className="h-3 w-3 text-emerald-600" /> <span className="text-emerald-700 font-semibold truncate">{match.result}</span></>
              : match.result ? <span className="truncate">{match.result}</span> : <><MapPin className="h-3 w-3" /> <span className="truncate">{match.venue}</span></>}
          </div>
        )}
      </div>

      {match.bettingSuspended && !isCompleted && (
        <div className="mx-2 mt-2 rounded-[3px] border border-amber-400 bg-amber-50 p-3 text-center" data-testid="match-suspended-banner">
          <div className="font-semibold text-amber-800 text-sm">Betting suspended</div>
          <div className="text-xs text-amber-700 mt-0.5">Odds are temporarily on hold for this match. Please check back shortly.</div>
        </div>
      )}

      {/* Match Odds */}
      <div className="px-2 pt-2.5">
        <span className="inline-block rounded-full bg-[#f5b400] text-black text-[13px] font-bold px-4 py-1.5 shadow" data-testid="match-odds-chip">Match Odds</span>
      </div>
      <div className="px-2 pt-2 space-y-2">
        {winner && renderWinner(winner)}
        {others.map((market) => (
          <div key={market.key} className="bg-white rounded-[3px] overflow-hidden shadow-sm">
            <div className="bg-[#1f3b5b] text-white px-3 py-2 text-[13px] font-semibold">{market.name}</div>
            {market.options.map((o) => {
              const active = selection && selection.marketKey === market.key && selection.selectionId === o.id;
              const isWinner = isCompleted && o.won;
              return (
                <div key={o.id} className="grid grid-cols-[1fr_84px] border-b border-slate-200 last:border-b-0">
                  <div className="px-3 py-2 text-[13px] font-medium text-slate-900 truncate">{o.label}{isWinner && <span className="ml-2 text-xs text-emerald-600">✓ Won</span>}</div>
                  <button onClick={() => pick(market, o)} disabled={bettingDisabled}
                    className={`h-[46px] grid place-items-center font-display font-extrabold text-[16px] text-slate-900 transition-colors disabled:opacity-60 ${active ? "bg-[#5AAEEA] ring-2 ring-inset ring-sky-800" : "bg-[#72BBEF] hover:bg-[#5AAEEA]"}`}>
                    <Odds value={o.odds} showArrow />
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Category tabs: Session | Lambi | Toss */}
      {tabs.length > 0 && (
        <>
          <div className="mt-3 flex items-center gap-1 bg-[#2b2f36] px-1.5 py-1.5" data-testid="market-tabs">
            {tabs.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`market-tab-${t.key}`}
                className={`flex items-center gap-1.5 px-4 py-1.5 text-[13px] font-bold transition-colors ${activeTab === t.key ? "bg-[#f5b400] text-black" : "bg-[#3a3f47] text-white"}`}>
                {t.key !== "toss" && <span className={`grid place-items-center h-4 w-4 rounded-[3px] text-[10px] font-extrabold ${activeTab === t.key ? "bg-black text-[#f5b400]" : "bg-white text-black"}`}>F</span>}
                {t.label}
              </button>
            ))}
          </div>
          <div className="mx-2 mt-2 flex items-center gap-2 rounded-[3px] border-l-4 border-[#1e5bd6] bg-[#e8eef7] px-3 py-2 text-[12px] text-slate-700" data-testid="market-rules-bar">
            <Info className="h-4 w-4 text-[#1e5bd6] shrink-0" />
            <span>{activeTab === "toss" ? "Even money · closes 1 hour before start · settled after the toss." : "Yes wins at or above the line · No wins strictly below · even money."}</span>
          </div>
          <div className="px-2 pt-2 pb-4 space-y-2">
            {activeTab === "session" && <SessionMarket match={match} type="session" />}
            {activeTab === "lambi" && <SessionMarket match={match} type="lambi" />}
            {activeTab === "toss" && <TossMarket match={match} />}
          </div>
        </>
      )}
      {tabs.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-500">No session or lambi line right now.</div>}

      {/* Bet slip popup (opens on screen when a selection is made) */}
      <BetSlip
        selection={selection}
        outcomes={(() => {
          if (!selection || !match) return [];
          const mk = (match.markets || []).find((m) => m.key === selection.marketKey);
          return (mk?.options || []).map((o) => o.label);
        })()}
        liveOdds={(() => {
          if (!selection || !match) return null;
          const mk = (match.markets || []).find((m) => m.key === selection.marketKey);
          const opt = mk?.options.find((o) => o.id === selection.selectionId);
          if (!opt) return null;
          return selection.side === "lay" ? opt.lay : (opt.back ?? opt.odds);
        })()}
        onClear={() => setSelection(null)}
      />
    </div>
  );
}
